import React, { useState, useEffect, useRef } from "react";
import { Dropdown, InputGroup, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { ActionHandlerFunction, DateOutput } from "@preveil-api";
import { dayjs, UIManagerActionTypes, FilterStates, FilterStatesTypes, FilterToDateRange } from "src/common";
import { Icon } from "src/components";
import { DateSelector } from ".";

type AllProps = {
    default_value?: DateOutput;
    default_active?: FilterStatesTypes;
    include_forever?: boolean;
    handleAction: ActionHandlerFunction;
}
const now = dayjs().endOf("day").toDate();
const default_start = dayjs().startOf("day").subtract(2, "week").toDate();
function DateFilterComponent(props: AllProps) {
    const { default_value, default_active, include_forever, handleAction } = props;
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [show_dropdown, setShowDropdown] = useState<boolean>(false);
    const [start_date, setStartDate] = useState<DateOutput>(default_value !== undefined ? default_value : default_start);
    const [end_date, setEndDate] = useState<DateOutput>(default_value !== undefined ? default_value : now);
    const [active_item, setActiveItem] = useState<FilterStatesTypes | undefined>(default_active);
    const [show_date_picker, setShowDatePicker] = useState<boolean>(false);

    // Description: Handle the Hide and show of the filter dropdown
    useEffect(() => {
        // NOTE: Pass a default active Item
        !!default_active && handleSetRange([FilterToDateRange[default_active].toDate(), now]);

        function handleClickOutside(event: any) {
            (!!dropdownRef.current && !dropdownRef.current.contains(event.target)) && handleCancel();
        }
        // Description: Bind the outside event listener for closing the filter dropdown
        document.addEventListener("mousedown", handleClickOutside);
        return () => { // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Description: Handles OnClick of the main DropdownItems
    function handleClick(date: dayjs.Dayjs, active_item: FilterStatesTypes) {
        handleSetRange([date.toDate(), now]);
        setShowDropdown(!show_dropdown);
        setActiveItem(active_item);
        handleAction({
            actionType: UIManagerActionTypes.DateSelector,
            params: { start: date.toDate(), end: now, selected_range: active_item },
        });
    };


    // Description: handles Changes on the datepicker or the main DropdownItems click
    function handleChange(dates: [Date, Date]) {
        handleSetRange(dates);
        setShowDatePicker(true);
    };

    // Description: set range dates in state
    function handleSetRange(dates: [Date, Date]) {
        const [start, end] = dates;
        setStartDate(!!start ? dayjs(start).startOf("day").toDate() : start);
        setEndDate(!!end ? dayjs(end).endOf("day").toDate() : end);
    }

    // Description: Handles Apply button click
    function handleApply() {
        setShowDatePicker(false);
        setShowDropdown(false);
        handleAction({
            actionType: UIManagerActionTypes.DateSelector,
            params: { start: start_date, end: end_date, selected_range: active_item },
        });
    };

    // Description: Cancel selections
    function handleCancel() {
        setShowDropdown(false);
        setShowDatePicker(false);
        setActiveItem(undefined);
    }

    // Description: handles clearing the values to default
    function handleReset() {
        const start = default_value !== undefined ? default_value : default_start;
        const end = default_value !== undefined ? default_value : now;
        setStartDate(start);
        setEndDate(end);
        handleCancel();
        handleAction({
            actionType: UIManagerActionTypes.DateSelector,
            params: { start, end },
        });
    };

    // Description: Render the main links
    function renderListItems() {
        return <div>
            <Dropdown.Item
                className={active_item === FilterStates.last_week ? "active" : ""}
                onClick={() => handleClick(FilterToDateRange.last_week, FilterStates.last_week)}>
                Past Week
            </Dropdown.Item>
            <Dropdown.Item
                onClick={() => handleClick(FilterToDateRange.last_two_weeks, FilterStates.last_two_weeks)}
                className={active_item === FilterStates.last_two_weeks ? "active" : ""}  >
                Past Two Weeks
            </Dropdown.Item>
            <Dropdown.Item
                onClick={() => handleClick(FilterToDateRange.last_month, FilterStates.last_month)}
                className={active_item === FilterStates.last_month ? "active" : ""} >
                Past Month
            </Dropdown.Item>
            <Dropdown.Item
                onClick={() => handleClick(FilterToDateRange.last_six_months, FilterStates.last_six_months)}
                className={active_item === FilterStates.last_six_months ? "active" : ""}>
                Past 6 Months
            </Dropdown.Item>
            <Dropdown.Item
                onClick={() => handleClick(FilterToDateRange.last_year, FilterStates.last_year)}
                className={active_item === FilterStates.last_year ? "active" : ""}>
                Past Year
            </Dropdown.Item>
            {
                !!include_forever &&
                <Dropdown.Item
                    // When the "Forever" tab is selected, set the start date to 20180101 (release data of the product)
                    onClick={() => handleClick(dayjs("2018-01-01"), FilterStates.forever)}
                    className={active_item === FilterStates.forever ? "active" : ""}>
                    Forever
                </Dropdown.Item>
            }
            <Dropdown.Item
                className={active_item === FilterStates.custom_range ? "active" : ""}
                onClick={() => {
                    setShowDatePicker(true);
                    setActiveItem(FilterStates.custom_range);
                }}>Custom Range</Dropdown.Item>
        </div>;
    }

    // Description: Render custom date pickers
    function renderDatePicker() {
        return <>
            <DateSelector
                start_date={start_date || default_start}
                end_date={end_date}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleChange={handleChange} />
            <div className="btn-panel">
                <Button
                    variant="primary"
                    size="sm"
                    disabled={!start_date || !end_date}
                    onClick={() => handleApply()} >Apply</Button>
                <Button
                    variant="no-outline-primary"
                    size="sm"
                    onClick={() => handleCancel()}>Cancel</Button>
            </div>
        </>;
    }

    return <Dropdown className="pv-dropdown" show={show_dropdown} ref={dropdownRef}>
        <InputGroup
            size="sm"
            onClick={() => setShowDropdown(true)}>
            <InputGroup.Text>
                <Icon className="pv-icon-calendar" />
            </InputGroup.Text>
            <DatePicker
                className="form-control"
                selected={start_date}
                startDate={start_date}
                endDate={end_date}
                onChange={handleChange}
                popperClassName="d-none"
                selectsRange
                dateFormat="MMM d, yyyy"
                placeholderText="Select start date"
                maxDate={now}
                shouldCloseOnSelect={false} />
            {
                (!!start_date || !!end_date) &&
                <Button variant="icon" className="text-muted-light" size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleReset();
                    }}><Icon className="ficon-x-circle" /></Button>
            }
        </InputGroup>
        <Dropdown.Menu>
            {show_date_picker ? renderDatePicker() : renderListItems()}
        </Dropdown.Menu>
    </Dropdown>;
}
export default React.memo(DateFilterComponent);